import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Scale, DollarSign, CreditCard, AlertTriangle, CheckCircle2, ArrowLeft, Plus,
  FileSignature, Loader2, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import SignaturePad from '../components/SignaturePad';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function VendingReconciliationPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [signing, setSigning] = useState(null);           // reconciliation object
  const [signerName, setSignerName] = useState('');

  // New reconciliation form
  const [form, setForm] = useState({
    machine_id: '',
    period_start: '',
    period_end: '',
    cash_counted: '',
    cashless_settled: '',
    driver_email: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/reconcile`);
      setItems(data.items || []);
    } catch (e) { toast.error('Failed to load reconciliations'); }
  }, []);

  const loadMachines = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/machines`);
      setMachines(data.items || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); loadMachines(); }, [load, loadMachines]);

  const startNew = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 3600 * 1000);
    setForm({
      machine_id: machines[0]?.id || '',
      period_start: start.toISOString(),
      period_end: end.toISOString(),
      cash_counted: '',
      cashless_settled: '',
      driver_email: '',
      notes: '',
    });
    setShowNew(true);
  };

  const submitNew = async () => {
    if (!form.machine_id) { toast.error('Pick a machine'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/vending/reconcile`, {
        machine_id: form.machine_id,
        period_start: form.period_start,
        period_end: form.period_end,
        cash_counted_cents: Math.round(parseFloat(form.cash_counted || '0') * 100),
        cashless_settled_cents: Math.round(parseFloat(form.cashless_settled || '0') * 100),
        driver_email: form.driver_email || undefined,
        notes: form.notes || undefined,
      });
      toast.success(`Reconciliation created — status: ${data.status}`);
      setShowNew(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setSubmitting(false); }
  };

  const doSign = async (sig) => {
    if (!signing) return;
    try {
      await axios.post(`${API}/api/vending/reconcile/${signing.id}/sign`, {
        signature_data_url: sig,
        signer_name: signerName,
        signer_role: 'route_driver',
        accepted_terms: true,
      });
      toast.success('Signed & closed');
      setSigning(null);
      setSignerName('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Signature failed'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="reconciliation-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => navigate('/vending')} data-testid="recon-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Fleet
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Vending Fleet</div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Scale className="h-6 w-6 text-emerald-600" /> Cash &amp; Cashless Reconciliation</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="recon-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button size="sm" onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recon-new">
            <Plus className="h-3.5 w-3.5 mr-1" /> New Close-out
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Close-outs</CardTitle></CardHeader>
        <CardContent className="p-0">
          {items.length === 0 && <p className="text-sm text-gray-medium text-center py-8">No reconciliations yet — start a route close-out with &quot;New Close-out&quot;.</p>}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Machine</th>
                    <th className="text-left p-2">Period</th>
                    <th className="text-right p-2">DEX Cash</th>
                    <th className="text-right p-2">Counted</th>
                    <th className="text-right p-2">Cash Δ</th>
                    <th className="text-right p-2">DEX Cashless</th>
                    <th className="text-right p-2">Settled</th>
                    <th className="text-right p-2">Cashless Δ</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r.id} className="border-b border-gray-100" data-testid={`recon-row-${r.id}`}>
                      <td className="p-2 font-semibold">{r.chassis_serial}</td>
                      <td className="p-2 text-gray-medium">{new Date(r.period_start).toLocaleDateString()} → {new Date(r.period_end).toLocaleDateString()}</td>
                      <td className="p-2 text-right">{fmtCents(r.dex_cash_cents)}</td>
                      <td className="p-2 text-right">{fmtCents(r.cash_counted_cents)}</td>
                      <td className={`p-2 text-right font-bold ${r.cash_variance_cents === 0 ? '' : r.cash_variance_cents > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtCents(r.cash_variance_cents)}</td>
                      <td className="p-2 text-right">{fmtCents(r.dex_cashless_cents)}</td>
                      <td className="p-2 text-right">{fmtCents(r.cashless_settled_cents)}</td>
                      <td className={`p-2 text-right font-bold ${r.cashless_variance_cents === 0 ? '' : r.cashless_variance_cents > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtCents(r.cashless_variance_cents)}</td>
                      <td className="p-2 text-center">
                        {r.status === 'balanced' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold"><CheckCircle2 className="h-2.5 w-2.5" />Balanced</span>}
                        {r.status === 'flagged' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"><AlertTriangle className="h-2.5 w-2.5" />Flagged</span>}
                        {r.status === 'closed' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-semibold"><CheckCircle2 className="h-2.5 w-2.5" />Closed</span>}
                      </td>
                      <td className="p-2 text-center">
                        {r.signed ? (
                          <span className="text-[10px] text-gray-medium">✓ {r.signer_name}</span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={() => { setSigning(r); setSignerName(r.driver_email?.split('@')[0] || ''); }} data-testid={`recon-sign-${r.id}`}>
                            <FileSignature className="h-3 w-3 mr-1" /> Sign
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New reconciliation dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Close-out</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-gray-medium">Machine</label>
            <select value={form.machine_id} onChange={(e) => setForm({ ...form, machine_id: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="recon-form-machine">
              <option value="">Pick machine…</option>
              {machines.map(m => <option key={m.id} value={m.id}>{m.chassis_serial || m.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-medium">Period start</label>
                <Input type="datetime-local" value={form.period_start.slice(0, 16)} onChange={(e) => setForm({ ...form, period_start: new Date(e.target.value).toISOString() })} data-testid="recon-form-start" />
              </div>
              <div>
                <label className="text-xs text-gray-medium">Period end</label>
                <Input type="datetime-local" value={form.period_end.slice(0, 16)} onChange={(e) => setForm({ ...form, period_end: new Date(e.target.value).toISOString() })} data-testid="recon-form-end" />
              </div>
              <div>
                <label className="text-xs text-gray-medium flex items-center gap-1"><DollarSign className="h-3 w-3" />Cash counted</label>
                <Input type="number" step="0.01" value={form.cash_counted} onChange={(e) => setForm({ ...form, cash_counted: e.target.value })} data-testid="recon-form-cash" />
              </div>
              <div>
                <label className="text-xs text-gray-medium flex items-center gap-1"><CreditCard className="h-3 w-3" />Cashless settled</label>
                <Input type="number" step="0.01" value={form.cashless_settled} onChange={(e) => setForm({ ...form, cashless_settled: e.target.value })} data-testid="recon-form-cashless" />
              </div>
            </div>
            <label className="text-xs text-gray-medium">Driver email (optional)</label>
            <Input value={form.driver_email} onChange={(e) => setForm({ ...form, driver_email: e.target.value })} data-testid="recon-form-driver" />
            <label className="text-xs text-gray-medium">Notes (optional)</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[60px] text-sm" data-testid="recon-form-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={submitNew} disabled={submitting} className="bg-emerald-600" data-testid="recon-form-submit">
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature dialog */}
      <Dialog open={!!signing} onOpenChange={(open) => { if (!open) { setSigning(null); setSignerName(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Sign to close-out</DialogTitle></DialogHeader>
          {signing && (
            <>
              <div className="text-xs text-gray-medium space-y-1 pb-2 border-b">
                <div>Machine: <b>{signing.chassis_serial}</b></div>
                <div>Total variance: <b className={signing.total_variance_cents === 0 ? 'text-emerald-600' : 'text-amber-600'}>{fmtCents(signing.total_variance_cents)}</b></div>
                <div className="text-[10px]">By signing you confirm the cash and cashless totals reported are accurate to the best of your knowledge.</div>
              </div>
              <SignaturePad signerName={signerName} setSignerName={setSignerName} onCapture={doSign} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
